import { Controller } from '@hotwired/stimulus';
import TomSelect from 'tom-select';

/**
 * Tom Select Controller
 *
 * Provides Tom Select dropdown with two modes:
 * 1. Static mode (default): Pre-defined options passed via data attributes
 * 2. Remote mode: Dynamic options loaded via AJAX from an API endpoint
 *
 * ## Modes
 *
 * ### Static Mode (Default)
 * Options are read directly from the original `<select>` element. Best for:
 * - Small, fixed lists of options
 * - Options that don't depend on user input
 * - Better performance (no API calls)
 *
 * ```html
 * <select data-controller="tom-select">
 *   <option value="">Select...</option>
 *   <option value="1">Option 1</option>
 *   <option value="2">Option 2</option>
 * </select>
 * ```
 *
 * ### Remote Mode
 * Options are loaded dynamically via AJAX. Best for:
 * - Large datasets that would be inefficient to load all at once
 * - Search/filter functionality
 * - Options that depend on user input or other dynamic factors
 *
 * ```html
 * <select data-controller="tom-select"
 *         data-tom-select-mode-value="remote"
 *         data-tom-select-url-value="/api/search"
 *         data-tom-select-search-key-value="q"
 *         data-tom-select-response-key-value="results"
 *         data-tom-select-label-field-value="name"
 *         data-tom-select-value-field-value="id">
 *   <option value="">Select...</option>
 * </select>
 * ```
 *
 * ## Targets
 * - select: The select element to enhance with Tom Select
 *
 * ## Values
 * - mode: "static" (default) or "remote" to determine behavior
 * - url: API endpoint for remote search (required for remote mode)
 * - searchKey: Query parameter key for search term (default: "q", remote mode only)
 * - responseKey: Key in response containing results (default: "results", remote mode only)
 * - labelField: Field in response containing label (default: "text")
 * - valueField: Field in response containing value (default: "value")
 * - maxItems: Maximum number of items that can be selected (default: null)
 * - removeButton: Whether to show the remove item button (default: true)
 * - clearButtonHtml: HTML for the clear button (default: '<i class="clear-button fas fa-xmark"></i>')
 */
export default class extends Controller {
  MODE_STATIC = 'static';

  MODE_REMOTE = 'remote';

  static values = {
    mode: { type: String, default: 'static' },
    url: String,
    searchKey: { type: String, default: 'q' },
    responseKey: { type: String, default: 'results' },
    labelField: { type: String, default: 'text' },
    valueField: { type: String, default: 'value' },
    maxItems: { type: Number, default: null },
    removeButton: { type: Boolean, default: true },
    clearButtonHtml: { type: String, default: '<i class="clear-button fas fa-xmark"></i>' },
    preload: { type: Boolean, default: false }
  }

  connect() {
    this.initTomSelect();
  }

  disconnect() {
    if (this.tomSelectInstance) {
      this.tomSelectInstance?.destroy();
      this.tomSelectInstance = null;
    }
  }

  initTomSelect() {
    const config = {
      maxItems: this.maxItemsValue,
      itemClass: 'item py-0 my-0',
      optionClass: 'option py-1 my-0',
      render: {
        item: (data, escape) => `<div data-ts-item>${escape(data.text || data.value || '')}</div>`,
        option: (data, escape) => `<div>${escape(data.text || data.value || '')}</div>`
      },
      onItemAdd: this.resetTextboxValue.bind(this),
      plugins: {
        ...(this.removeButtonValue && { remove_button: true }),
        clear_button: {
          html: () => this.clearButtonHtmlValue
        }
      }
    };

    if (this.modeValue === this.MODE_REMOTE) {
      config.load = this.loadRemoteOptions.bind(this);
      if (this.preloadValue) config.preload = true;
    }

    this.tomSelectInstance = new TomSelect(this.element, config);

    this.ensureOptionsHaveText();
  }

  resetTextboxValue() {
    this.tomSelectInstance.setTextboxValue('');
    this.tomSelectInstance.refreshOptions();
  }

  loadRemoteOptions(query, callback) {
    if (!this.hasUrlValue) {
      callback();
      return;
    }

    const url = new URL(this.urlValue, window.location.origin);
    url.searchParams.set(this.searchKeyValue, query);

    fetch(url)
      .then((response) => response.json())
      .then((data) => {
        const results = data[this.responseKeyValue] || data || [];
        const options = results.map((item) => ({
          text: item[this.labelFieldValue] || item,
          value: item[this.valueFieldValue] || item
        }));
        callback(options);
      })
      .catch(() => {
        callback();
      });
  }

  ensureOptionsHaveText() {
    Object.values(this.tomSelectInstance.options).forEach((option) => {
      if (!option.text) option.text = option.value;
    });
    this.tomSelectInstance.refreshOptions(false);
  }
}
